import {
  Select,
  SelectItem,
  SelectContent,
  SelectValue,
  SelectTrigger,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@altimateai/lego";
import { useMemo, useState } from "react";
import ClaudeCodeInstructions from "./ClaudeCodeInstructions";
import AltimateCodeInstructions from "./AltimateCodeInstructions";
import RemoteServerSetupInstructions from "./RemoteServerSetupInstructions";
import { Teammate } from "../mcp/mcpSlice";
import { useAppState } from "@/modules/app/useAppContext";

interface UseDatamateWithComponentProps {
  teammate?: Teammate;
}

const UseDatamateWithComponent = ({ teammate }: UseDatamateWithComponentProps) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const { enableAltimateCode } = useAppState();

  const options = useMemo(() => {
    const baseOptions = [
      {
        label: "Use in VS Code",
        value: "vscode",
        title: "VS Code Instructions",
        content: (
          <div>
            <iframe
              sandbox="allow-scripts allow-same-origin"
              src="https://app.supademo.com/embed/cmcsf7n170bq19st8fmtli26e?embed_v=2"
              style={{ width: "100%", height: "84vh" }}
              allowFullScreen
              title="VS Code Instructions"
            />
          </div>
        ),
      },
      {
        label: "Use in Cursor",
        value: "cursor",
        title: "Cursor Instructions",
        content: (
          <div>
            <iframe
              src="https://app.supademo.com/embed/cmcp46yex3a4r16b65z0c51as?embed_v=2"
              style={{ width: "100%", height: "84vh" }}
              allowFullScreen
              title="Cursor Instructions"
            />
          </div>
        ),
      },
      {
        label: "Use in Claude code",
        value: "claude-code",
        title: "Claude code Instructions",
        content: teammate ? (
          <ClaudeCodeInstructions teammate={teammate} />
        ) : (
          <div>Loading teammate data...</div>
        ),
      },
    ];

    if (enableAltimateCode) {
      baseOptions.push({
        label: "Use in Altimate Code",
        value: "altimate-code",
        title: "Altimate Code Instructions",
        content: teammate ? (
          <AltimateCodeInstructions teammate={teammate} />
        ) : (
          <div>Loading teammate data...</div>
        ),
      });
    }

    baseOptions.push({
      label: "Remote MCP Server Setup",
      value: "remote-mcp-server",
      title: "Remote MCP Server Setup Instructions",
      content: teammate ? (
        <RemoteServerSetupInstructions teammate={teammate} />
      ) : (
        <div>Loading teammate data...</div>
      ),
    });

    return baseOptions;
  }, [teammate, enableAltimateCode]);

  const handleChange = (value: string) => {
    setSelectedOption(value);
    setIsSheetOpen(true);
  };

  return (
    <div
      role="presentation"
      onClick={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
    >
      <Select onValueChange={handleChange}>
        <SelectTrigger>
          <SelectValue placeholder={options[0].label} />
        </SelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent side="right" className="!al-w-1/2 !al-max-w-[700px]">
          <SheetHeader>
            <SheetTitle>
              {options.find(option => option.value === selectedOption)?.title}
            </SheetTitle>
            <SheetDescription>
              {options.find(option => option.value === selectedOption)?.content}
            </SheetDescription>
          </SheetHeader>
          {/* Add your content here based on the selected option */}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default UseDatamateWithComponent;
